import React, { useState, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FiCheckSquare, FiSquare, FiCheckCircle, FiFlag, FiUserPlus, FiCalendar, FiTrash2, FiX, FiUser } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createTask, updateTask, deleteTask, bulkUpdateTasks, bulkDeleteTasks } from '../services/firestore';
import { useTask } from '../contexts/TaskContext';
import toast from 'react-hot-toast';

const COLUMNS = [
  { key: 'planificacion', label: 'PLANIFICACIÓN', color: '#dc2626', bgColor: '#fee2e2' },
  { key: 'en-curso', label: 'EN CURSO', color: '#2563eb', bgColor: '#dbeafe' },
  { key: 'finalizado', label: 'FINALIZADA', color: '#16a34a', bgColor: '#dcfce7' },
];

const KanbanBoardNew = () => {
  const { user } = useAuth();
  const {
    filteredTasks: tasks = [],
    selectedProjectId,
    selectedFolderId,
    selectedListId,
    setSelectedTaskId,
    setShowTaskDrawer,
    users = [],
  } = useTask();

  const [addingInColumn, setAddingInColumn] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const handleAddTask = async (columnKey) => {
    if (!newTaskTitle.trim() || !user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
    try {
      await createTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
        titulo: newTaskTitle.trim(),
        estado: columnKey,
      });
      setNewTaskTitle('');
      setAddingInColumn(null);
      toast.success('Tarea creada');
    } catch {
      toast.error('Error al crear tarea');
    }
  };

  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkPriorityOpen, setBulkPriorityOpen] = useState(false);
  const [bulkAssigneeOpen, setBulkAssigneeOpen] = useState(false);
  const [bulkDateOpen, setBulkDateOpen] = useState(false);

  const groupedTasks = useMemo(() => {
    const grouped = {};
    COLUMNS.forEach((col) => {
      grouped[col.key] = tasks.filter((t) => t.estado === col.key);
    });
    return grouped;
  }, [tasks]);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const taskId = draggableId;
    const newEstado = destination.droppableId;

    try {
      await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, taskId, { estado: newEstado });
      toast.success('Tarea movida');
    } catch (error) {
      toast.error('Error al mover tarea');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await deleteTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, taskId);
      toast.success('Tarea eliminada');
    } catch (error) {
      toast.error('Error al eliminar tarea');
    }
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setShowTaskDrawer(true);
  };

  const handleSelectTask = (e, taskId) => {
    e.stopPropagation();
    const newSelection = new Set(selectedTasks);
    if (newSelection.has(taskId)) newSelection.delete(taskId);
    else newSelection.add(taskId);
    setSelectedTasks(newSelection);
  };

  const closeAllBulkMenus = () => {
    setBulkStatusOpen(false);
    setBulkPriorityOpen(false);
    setBulkAssigneeOpen(false);
    setBulkDateOpen(false);
  };

  const handleBulkStatusChange = async (newStatus) => {
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, [...selectedTasks], { estado: newStatus });
      toast.success(`${selectedTasks.size} tareas actualizadas`);
      setSelectedTasks(new Set());
      closeAllBulkMenus();
    } catch { toast.error('Error al actualizar'); }
  };

  const handleBulkPriorityChange = async (p) => {
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, [...selectedTasks], { prioridad: p });
      toast.success(`${selectedTasks.size} tareas actualizadas`);
      setSelectedTasks(new Set());
      closeAllBulkMenus();
    } catch { toast.error('Error al actualizar'); }
  };

  const handleBulkAssigneeChange = async (email) => {
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, [...selectedTasks], { asignados: email ? [email] : [] });
      toast.success(`${selectedTasks.size} tareas asignadas`);
      setSelectedTasks(new Set());
      closeAllBulkMenus();
    } catch { toast.error('Error al asignar'); }
  };

  const handleBulkDateChange = async (d) => {
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, [...selectedTasks], { fechaVencimiento: d });
      toast.success(`${selectedTasks.size} tareas actualizadas`);
      setSelectedTasks(new Set());
      closeAllBulkMenus();
    } catch { toast.error('Error al actualizar'); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`¿Eliminar ${selectedTasks.size} tareas?`)) return;
    try {
      await bulkDeleteTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, [...selectedTasks]);
      toast.success(`${selectedTasks.size} tareas eliminadas`);
      setSelectedTasks(new Set());
    } catch { toast.error('Error al eliminar'); }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    if (typeof date === 'object' && date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
    }
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
    }
    return '-';
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex h-full overflow-x-auto gap-3 sm:gap-6 p-3 sm:p-6 bg-white dark:bg-gray-900">
          {COLUMNS.map((column) => {
            const columnTasks = groupedTasks[column.key] || [];

            return (
              <div
                key={column.key}
                className="flex-shrink-0 w-72 sm:w-80 flex flex-col bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 max-h-full"
              >
                {/* Column Header */}
                <div className="p-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: column.color }} />
                    <span className="font-semibold text-gray-700 dark:text-gray-200 uppercase text-xs tracking-wider">
                      {column.label}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                      {columnTasks.length}
                    </span>
                  </div>
                  <button
                    onClick={() => { setAddingInColumn(column.key); setNewTaskTitle(''); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Agregar tarea"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {/* Droppable Column */}
                <Droppable droppableId={column.key} type="TASK">
                  {(provided, snapshot) => (
                    <div
                      className={`flex-1 overflow-y-auto p-3 transition-colors ${snapshot.isDraggingOver ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      <div className="flex flex-col gap-3">
                        {columnTasks.map((task, index) => {
                          const isSelected = selectedTasks.has(task.id);
                          return (
                            <Draggable key={task.id} draggableId={task.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border transition-all cursor-grab active:cursor-grabbing group ${
                                    isSelected
                                      ? 'border-blue-400 ring-2 ring-blue-400/20 bg-blue-50/50 dark:bg-blue-900/20'
                                      : 'border-gray-100 dark:border-gray-700 hover:shadow-md'
                                  } ${snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500/20 rotate-2' : ''}`}
                                  onClick={() => handleTaskClick(task.id)}
                                >
                                  <div className="flex items-start justify-between gap-2 mb-3">
                                    {/* Selection checkbox */}
                                    <button
                                      onClick={(e) => handleSelectTask(e, task.id)}
                                      className="flex-shrink-0 mt-0.5 transition-all"
                                    >
                                      {isSelected ? (
                                        <FiCheckSquare size={15} className="text-blue-600" />
                                      ) : (
                                        <FiSquare size={15} className="text-gray-300 opacity-0 group-hover:opacity-100 hover:text-gray-500" />
                                      )}
                                    </button>
                                    <h3 className="flex-1 font-medium text-gray-900 dark:text-gray-100 text-sm leading-snug break-words line-clamp-3">
                                      {task.titulo}
                                    </h3>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                      className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 -mr-2 -mt-2"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>

                                  {/* Task Metadata */}
                                  <div className="flex items-center justify-between pt-3 border-t border-gray-50 dark:border-gray-700/50">
                                    <div className="flex items-center gap-2">
                                      {task.asignados && task.asignados.length > 0 ? (
                                        <div className="flex -space-x-2">
                                          {task.asignados.slice(0, 3).map((email, i) => (
                                            <div key={i} className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-xs flex items-center justify-center font-medium border-2 border-white dark:border-gray-800" title={email}>
                                              {email.charAt(0).toUpperCase()}
                                            </div>
                                          ))}
                                          {task.asignados.length > 3 && (
                                            <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs flex items-center justify-center font-medium border-2 border-white dark:border-gray-800">
                                              +{task.asignados.length - 3}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400">
                                          <span className="text-[10px]">+</span>
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                                      {formatDate(task.fechaVencimiento)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                      </div>
                      {provided.placeholder}

                      {addingInColumn === column.key ? (
                        <div className="mt-3 bg-white dark:bg-gray-800 rounded-lg border border-blue-300 dark:border-blue-600 shadow-sm p-2">
                          <input
                            type="text"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddTask(column.key);
                              if (e.key === 'Escape') { setAddingInColumn(null); setNewTaskTitle(''); }
                            }}
                            placeholder="Nombre de la tarea..."
                            className="w-full px-2 py-1.5 text-sm border-0 focus:outline-none bg-transparent text-gray-800 dark:text-gray-200 placeholder-gray-400"
                            autoFocus
                          />
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <button
                              onClick={() => handleAddTask(column.key)}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700"
                            >
                              Crear
                            </button>
                            <button
                              onClick={() => { setAddingInColumn(null); setNewTaskTitle(''); }}
                              className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setAddingInColumn(column.key); setNewTaskTitle(''); }}
                          className="mt-3 w-full py-2 flex items-center justify-center gap-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-gray-700 border-dashed"
                        >
                          <Plus size={16} />
                          <span>Añadir tarea</span>
                        </button>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Bulk Actions Toolbar */}
      {selectedTasks.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-gray-900 dark:bg-gray-800 text-white px-4 py-2.5 rounded-xl shadow-2xl border border-gray-700 animate-slide-up">
          <span className="text-sm font-medium text-blue-400 mr-1">
            {selectedTasks.size} seleccionada{selectedTasks.size > 1 ? 's' : ''}
          </span>
          <div className="w-px h-5 bg-gray-600" />

          {/* Status */}
          <div className="relative">
            <button onClick={() => { closeAllBulkMenus(); setBulkStatusOpen(!bulkStatusOpen); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors">
              <FiCheckCircle size={14} /> Estado
            </button>
            {bulkStatusOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 min-w-[160px]">
                {[
                  { key: 'planificacion', label: 'Planificación', color: 'bg-gray-400' },
                  { key: 'en-curso', label: 'En Curso', color: 'bg-blue-500' },
                  { key: 'finalizado', label: 'Finalizado', color: 'bg-green-500' },
                ].map(s => (
                  <button key={s.key} onClick={() => handleBulkStatusChange(s.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} /> {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Priority */}
          <div className="relative">
            <button onClick={() => { closeAllBulkMenus(); setBulkPriorityOpen(!bulkPriorityOpen); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors">
              <FiFlag size={14} /> Prioridad
            </button>
            {bulkPriorityOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 min-w-[140px]">
                {[
                  { key: 'alta', label: 'Alta', color: 'bg-red-500' },
                  { key: 'media', label: 'Media', color: 'bg-yellow-500' },
                  { key: 'baja', label: 'Baja', color: 'bg-green-500' },
                ].map(p => (
                  <button key={p.key} onClick={() => handleBulkPriorityChange(p.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <div className={`w-2.5 h-2.5 rounded-full ${p.color}`} /> {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assignee */}
          <div className="relative">
            <button onClick={() => { closeAllBulkMenus(); setBulkAssigneeOpen(!bulkAssigneeOpen); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors">
              <FiUserPlus size={14} /> Asignar
            </button>
            {bulkAssigneeOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 min-w-[200px] max-h-[200px] overflow-y-auto">
                <button onClick={() => handleBulkAssigneeChange(null)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FiUser size={14} /> Sin asignar
                </button>
                {users.map(u => (
                  <button key={u.email} onClick={() => handleBulkAssigneeChange(u.email)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                    {u.photoURL ? (
                      <img src={u.photoURL} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                        {(u.displayName || u.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="truncate">{u.displayName || u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date */}
          <div className="relative">
            <button onClick={() => { closeAllBulkMenus(); setBulkDateOpen(!bulkDateOpen); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors">
              <FiCalendar size={14} /> Fecha
            </button>
            {bulkDateOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 min-w-[160px]">
                {[
                  { label: 'Hoy', days: 0 },
                  { label: 'Mañana', days: 1 },
                  { label: 'Próxima semana', days: 7 },
                  { label: 'En 2 semanas', days: 14 },
                  { label: 'En 1 mes', days: 30 },
                  { label: 'Sin fecha', days: null },
                ].map(d => (
                  <button key={d.label} onClick={() => {
                    if (d.days === null) handleBulkDateChange(null);
                    else {
                      const date = new Date();
                      date.setDate(date.getDate() + d.days);
                      handleBulkDateChange(date.toISOString());
                    }
                  }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-left">
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-5 bg-gray-600" />
          <button onClick={handleBulkDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-red-400 hover:bg-red-900/30 transition-colors">
            <FiTrash2 size={14} /> Eliminar
          </button>
          <button onClick={() => setSelectedTasks(new Set())}
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-700 transition-colors ml-1">
            <FiX size={14} />
          </button>
        </div>
      )}
    </>
  );
};

export default KanbanBoardNew;
